/**
 * @file AddEditVendor.js
 * @description A component to add or edit a Vendor.
 */
import React, { useState, useEffect } from 'react';
import {
    fetchVendors,
    fetchVendorById,
    createVendor,
    updateVendor,
} from '../../services/systemService';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { useNavigate, useParams } from 'react-router';
import { Dropdown } from '../helpers/Dropdown';
import { isValidUrl } from '../../utils/utils';
import styles from './Vendors.module.scss';
import { ROUTES } from '../../constants/routes';
import { useAuthContext } from '../../store/Contexts';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

const AddEditVendor = () => {
    const navigate = useNavigate();
    const { vendorId } = useParams();
    const [allVendors, setAllVendors] = useState([]);
    const isNewVendor = location.pathname.includes('add');
    const [isLoading, setIsLoading] = useState(!isNewVendor ?? true);
    const [vendorName, setVendorName] = useState('');
    const [vendorDetails, setVendorDetails] = useState({
        name: '',
        code: '',
        vendorCategoryId: '',
        website: '',
        tenantId: window.localStorage.getItem('tenantId'),
    });

    const vendorCategoryDropdownOptions = [
        { value: 1, label: getText('msg_387996d68a47') },
        { value: 2, label: getText('msg_d45987441aa0') },
        { value: 3, label: getText('msg_3bdcefa3d984') },
        { value: 4, label: getText('msg_91b0ba24657f') },
        { value: 5, label: getText('msg_182317176ec0') },
        { value: 6, label: getText('msg_3ac645f1d486') },
    ];

    const { hasAttribute } = useAuthContext();

    const canEditForm = hasAttribute(SecurityAttributes.VendorFullAccess);

    const getVendorCategoryLabelDropdown = categoryId => {
        const category = vendorCategoryDropdownOptions.find(
            option => option.value === categoryId,
        );
        return category ? category.label : 'Select Category';
    };

    const handleVendorCategoryChange = categoryId => {
        setVendorDetails(prevDetails => ({
            ...prevDetails,
            vendorCategoryId: categoryId,
        }));
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setVendorDetails(prevDetails => ({ ...prevDetails, [name]: value }));
    };

    const handleClose = () => {
        navigate(ROUTES.VENDORS);
    };

    const loadVendors = async () => {
        try {
            const fetchedVendors = await fetchVendors();
            setAllVendors(Array.isArray(fetchedVendors) ? fetchedVendors : []);
        } catch (error) {
            console.error('Failed to get Vendors.', error);
            Toast.error(getText('msg_12a7d3c052bc'));
        } finally {
            setIsLoading(false);
        }
    };

    const validateForm = () => {
        if (!vendorDetails.name || vendorDetails.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }
        if (!vendorDetails.code || vendorDetails.code.trim() === '') {
            Toast.error(getText('msg_ea6ac41a6ab6'));
            return false;
        }
        if (
            !vendorDetails.vendorCategoryId ||
            vendorDetails.vendorCategoryId === ''
        ) {
            Toast.error(getText('msg_25c854effb16'));
            return false;
        }
        if (
            vendorDetails.website !== '' &&
            !isValidUrl(vendorDetails.website)
        ) {
            Toast.error(getText('msg_5ccc853d1171'));
            return false;
        }

        // Check if the name of the newly created vendor is already within the database, if so, reject
        const checkIfVendorNameExists = allVendors.some(vendor => {
            const existingName = vendor?.name?.toLowerCase() || '';
            const currentName = vendorDetails?.name?.toLowerCase() || '';
            return (
                existingName === currentName &&
                (isNewVendor || vendor.id !== vendorDetails.id)
            );
        });

        if (checkIfVendorNameExists) {
            Toast.error(
                getText('msg_915381f1c295'),
                null,
                getText('msg_456bc1bf0716'),
            );
            return false;
        }

        // Check if the code of the newly created vendor is already within the database, if so, reject
        const checkIfVendorCodeExists = allVendors.some(vendor => {
            const existingCode = vendor?.code?.toLowerCase() || '';
            const currentCode = vendorDetails?.code?.toLowerCase() || '';
            return (
                existingCode === currentCode &&
                (isNewVendor || vendor.id !== vendorDetails.id)
            );
        });

        if (checkIfVendorCodeExists) {
            Toast.error(
                getText('msg_75cc5b975346'),
                null,
                getText('msg_bdb98cea5241'),
            );
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        try {
            const vendorPayload = {
                id: isNewVendor ? 0 : vendorDetails.id,
                code: vendorDetails.code,
                name: vendorDetails.name,
                vendorCategoryId: vendorDetails.vendorCategoryId,
                website: vendorDetails.website,
            };

            if (isNewVendor) {
                await createVendor(vendorPayload);
                Toast.success(getText('msg_812b29a09345'));
            } else {
                await updateVendor(vendorPayload);
                Toast.success(getText('msg_cd564e049696'));
            }

            navigate(ROUTES.VENDORS);
        } catch (error) {
            console.error('Error saving vendor:', error);
            Toast.error(getText('msg_65fa19a93ce5'));
        }
    };

    useEffect(() => {
        const fetchVendor = async () => {
            try {
                const vendorToEdit = await fetchVendorById(vendorId);
                setVendorDetails(vendorToEdit);
                setVendorName(vendorToEdit?.name);
            } catch (error) {
                console.error('Failed to fetch individual Vendor.', error);
                Toast.error(
                    getText('msg_0d395fe22134'),
                );
            } finally {
                setIsLoading(false);
            }
        };

        if (vendorId) {
            fetchVendor();
        }
        loadVendors();
    }, [vendorId]);

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4 b">
                    <div className="d-flex justify-content-between align-items-center pb-3 border-bottom">
                        <p className="page-title float-start card-title ms-4 mb-0">
                            {isNewVendor ? 'Add New Vendor' : `${vendorName}`}
                        </p>
                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={handleClose}
                                className="ing-text-buttons mx-4"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    <form
                        onSubmit={e => e.preventDefault()}
                        className={`m-auto col-xs-12 col-sm-12 col-md-10 col-lg-8 px-4 py-5`}
                    >
                        <section className="card-title d-flex flex-wrap mb-5 overflow-visible flex-column">
                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="name">
                                    {getText('msg_06ae0d4f8a00')}
                                    <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="name"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={vendorDetails.name}
                                    onChange={handleChange}
                                    name="name"
                                    placeholder={getText('msg_87da86ee7ec7')}
                                    required
                                />
                            </div>

                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="code">
                                    {getText('msg_00e0dbcb1b8a')}
                                    <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="code"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={vendorDetails.code}
                                    onChange={handleChange}
                                    name="code"
                                    placeholder={getText('msg_500266b85586')}
                                    required
                                />
                            </div>

                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="category">
                                    {getText('msg_a3c686e711e4')}
                                    <span className="text-danger">*</span>
                                </label>
                                <Dropdown
                                    defaultValue={getVendorCategoryLabelDropdown(
                                        vendorDetails?.vendorCategoryId,
                                    )}
                                    options={vendorCategoryDropdownOptions}
                                    onChange={handleVendorCategoryChange}
                                />
                            </div>

                            <div
                                className={`form-group col-12 col-sm-12 col-md-12 col-lg-12 mb-4`}
                            >
                                <label htmlFor="website">{getText('msg_2e8a57cc5c47')}</label>
                                <input
                                    id="website"
                                    type="text"
                                    maxLength="50"
                                    className={`form-control`}
                                    value={vendorDetails.website}
                                    onChange={handleChange}
                                    name="website"
                                    placeholder={getText('msg_53018a5f0d0c')}
                                    required
                                />
                            </div>
                        </section>

                        {canEditForm && (
                            <div className="d-flex justify-content-between">
                                <span
                                    onClick={handleClose}
                                    type="button"
                                    className="ms-3 button-base ing-button-primary-left align-left"
                                >
                                    {getText('msg_77dfd2135f4d')}
                                </span>
                                <span
                                    onClick={handleSave}
                                    type="button"
                                    className="ms-3 button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-check-lg me-2" />
                                    {getText('msg_efc007a393f6')}
                                </span>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddEditVendor;
